import { useEffect, useState, useCallback } from 'react';
import { adminGet } from './adminApi';
import { repGet, repPut } from './reports/reportsApi';
import { toast } from 'sonner';

const GREEN = '#0B6B55';

export default function PlannedStopsCard({ tripId, tenantId }) {
  const [stops, setStops] = useState([]);
  const [sites, setSites] = useState([]);
  const [pick, setPick] = useState('');
  const [freeText, setFreeText] = useState('');
  const [dirty, setDirty] = useState(false);

  const load = useCallback(() => {
    repGet(`/trips/${tripId}/planned-stops`).then(s => { setStops(s); setDirty(false); }).catch(() => {});
    if (tenantId) adminGet(`/customers/${tenantId}/org-units`)
      .then(ous => setSites(ous.filter(o => o.is_site))).catch(() => setSites([]));
  }, [tripId, tenantId]);
  useEffect(() => { load(); }, [load]);

  const add = () => {
    const site = sites.find(s => s.id === pick);
    const name = site ? site.name : freeText.trim();
    if (!name) return;
    setStops([...stops, { site_org_unit_id: site?.id || null, site_name: name }]);
    setPick(''); setFreeText(''); setDirty(true);
  };
  const move = (i, dir) => {
    const next = [...stops];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setStops(next); setDirty(true);
  };
  const remove = (i) => { setStops(stops.filter((_, x) => x !== i)); setDirty(true); };
  const save = async () => {
    try {
      const out = await repPut(`/trips/${tripId}/planned-stops`, {
        stops: stops.map(s => ({ site_org_unit_id: s.site_org_unit_id, site_name: s.site_name })) });
      setStops(out); setDirty(false);
      toast.success('Planned stops saved — the routing report will flag missed stops');
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="rounded-lg border bg-white p-5 mb-6" style={{ borderColor: '#E5E3DC' }} data-testid="planned-stops-card">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-bold text-lg" style={{ color: '#1B1D1C' }}>Planned stops</h2>
        {dirty && (
          <button onClick={save} data-testid="planned-stops-save-btn"
            className="px-3 py-1.5 rounded-md text-sm text-white font-medium" style={{ background: GREEN }}>Save order</button>
        )}
      </div>
      <p className="text-xs mb-3" style={{ color: '#9a9e96' }}>
        Ordered branch/site stops. The routing report compares them against actual halts and flags missed stops.
      </p>
      {stops.length === 0 && <p className="text-sm mb-3" style={{ color: '#9a9e96' }}>No planned stops yet</p>}
      <ol className="mb-3 space-y-1.5">
        {stops.map((s, i) => (
          <li key={`${s.site_org_unit_id || s.site_name}-${i}`} className="flex items-center gap-2 text-sm rounded-md border px-3 py-1.5"
            style={{ borderColor: '#F0EEE8' }} data-testid={`planned-stop-${i}`}>
            <span className="font-bold w-5" style={{ color: GREEN }}>{i + 1}</span>
            <span className="flex-1">{s.site_name}{!s.site_org_unit_id && <span className="text-xs" style={{ color: '#9a9e96' }}> (free text)</span>}</span>
            <button onClick={() => move(i, -1)} className="text-xs px-1 hover:underline" style={{ color: '#6b7069' }} data-testid={`stop-up-${i}`}>↑</button>
            <button onClick={() => move(i, 1)} className="text-xs px-1 hover:underline" style={{ color: '#6b7069' }} data-testid={`stop-down-${i}`}>↓</button>
            <button onClick={() => remove(i)} className="text-xs px-1 hover:underline" style={{ color: '#B3401F' }} data-testid={`stop-remove-${i}`}>Remove</button>
          </li>
        ))}
      </ol>
      <div className="flex gap-2 items-center">
        <select data-testid="planned-stop-site-select" value={pick} onChange={e => setPick(e.target.value)}
          className="h-8 rounded-md border px-2 text-sm bg-white w-56" style={{ borderColor: '#D9D6CD' }}>
          <option value="">— pick a site —</option>
          {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <span className="text-xs" style={{ color: '#9a9e96' }}>or</span>
        <input data-testid="planned-stop-name-input" value={freeText} onChange={e => setFreeText(e.target.value)}
          placeholder="Free-text stop name" className="h-8 rounded-md border px-2 text-sm w-48" style={{ borderColor: '#D9D6CD' }} />
        <button onClick={add} disabled={!pick && !freeText.trim()} data-testid="planned-stop-add-btn"
          className="h-8 px-3 rounded-md text-sm text-white font-medium disabled:opacity-50" style={{ background: GREEN }}>Add stop</button>
      </div>
    </div>
  );
}
